import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { closeChat, useChatOpen } from "../../lib/chat";
import {
  type AnswerReceipts,
  type ChatSession,
  startSession,
  streamMessage,
} from "../../lib/chatApi";
import { api } from "../../lib/api";
import { ContactActions, splitContact } from "./ContactActions";

/**
 * The assistant panel.
 *
 * It answers from the same content the rest of the site renders, and it
 * refuses everything else — the refusal rate is a published number, not a
 * suppressed error. Everything the server reports about *how* an answer was
 * produced is shown under it: which model, how long, whether it fell back,
 * and which pages it drew on. A chatbot that shows its receipts is making a
 * checkable claim; one that doesn't is asking to be trusted.
 *
 * The dialog semantics were built before the transport and are unchanged:
 * `role="dialog"`, a label, focus in on open and restored on close, Tab
 * trapped in both directions, Escape. Retrofitting those onto a finished
 * visual design is how modals end up inaccessible.
 *
 * **The reply streams into an `aria-live="polite"` region, not an assertive
 * one.** Assertive would interrupt a screen reader on every token — dozens of
 * interruptions per answer. Polite queues it, which is what a stream of text
 * arriving actually warrants.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Past this, a cold free-tier backend is probably waking rather than broken. */
const WAKING_AFTER_MS = 2000;

interface Turn {
  role: "user" | "assistant";
  text: string;
  receipts?: AnswerReceipts;
  error?: boolean;
}

/**
 * The receipts are collected and deliberately not shown.
 *
 * Model name, latency and source slugs are engineering telemetry — useful to
 * whoever built this, noise to a recruiter deciding whether to email. They
 * still arrive on every `done` frame and are still persisted server-side,
 * which is what /chat-stats reports from; the panel simply stops narrating
 * them. Nothing was removed from the pipeline, only from the reading
 * experience.
 */

function Thinking({ reduced }: { reduced: boolean }) {
  return (
    <span className="inline-flex gap-1 align-middle">
      {[0, 1, 2].map((dot) => (
        <motion.span
          key={dot}
          className="size-1.5 rounded-full bg-accent"
          animate={reduced ? undefined : { opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1.2, repeat: Infinity, delay: dot * 0.18, ease: "easeInOut" }}
        />
      ))}
      <span className="sr-only">Thinking…</span>
    </span>
  );
}

/**
 * One answer, with its contact sentence turned into buttons.
 *
 * The rewrite waits until the answer has settled. Mid-stream the address
 * arrives a character at a time, so stripping a half-written sentence would
 * make the paragraph reflow on nearly every frame — text jumping around while
 * someone is reading it is worse than the plain address it replaces.
 */
function AnswerBubble({
  turn,
  linkedinUrl,
  reduced,
}: {
  turn: Turn;
  linkedinUrl: string | null | undefined;
  reduced: boolean;
}) {
  const settled = turn.receipts !== undefined || turn.error === true;
  const { prose, hasContact } = settled
    ? splitContact(turn.text)
    : { prose: turn.text, hasContact: false };

  return (
    <>
      <p
        className={`rounded-2xl rounded-bl-sm border px-3.5 py-2.5 text-2xs leading-relaxed whitespace-pre-wrap ${
          turn.error ? "border-halt/40 bg-canvas text-halt" : "border-edge bg-canvas text-text"
        }`}
      >
        {/* An answer that was *only* the contact sentence leaves nothing to
            print, and an empty bubble above two buttons reads as a bug. */}
        {prose || (turn.text ? "Here's the quickest way to reach him:" : <Thinking reduced={reduced} />)}
      </p>
      {hasContact && <ContactActions linkedinUrl={linkedinUrl} />}
    </>
  );
}

function Panel() {
  const reduced = useReducedMotion() ?? false;
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const logRef = useRef<HTMLDivElement>(null);

  const [session, setSession] = useState<ChatSession | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [waking, setWaking] = useState(false);
  // For the LinkedIn button on a contact answer. Read from the profile rather
  // than hard-coded, so changing the link in the admin changes it here too —
  // and a failure costs one button, never the conversation.
  const [linkedinUrl, setLinkedinUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .profile()
      .then((profile) => {
        if (!cancelled) setLinkedinUrl(profile.linkedin_url);
      })
      .catch(() => {
        /* no LinkedIn button; the email one still works */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Session ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const controller = new AbortController();
    const wakingTimer = setTimeout(() => { setWaking(true); }, WAKING_AFTER_MS);

    void startSession()
      .then((next) => {
        if (controller.signal.aborted) return;
        setSession(next);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setSessionError(
          "Couldn't start a conversation. The server may be waking up — reopen in a few seconds.",
        );
      })
      .finally(() => {
        clearTimeout(wakingTimer);
        setWaking(false);
      });

    return () => {
      controller.abort();
      clearTimeout(wakingTimer);
    };
  }, []);

  // ── Dialog behaviour ─────────────────────────────────────────────────────
  useEffect(() => {
    closeRef.current?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeChat();
        return;
      }
      if (event.key !== "Tab") return;

      const panel = panelRef.current;
      if (!panel) return;
      const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((node) =>
        node.checkVisibility({ opacityProperty: true, visibilityProperty: true }),
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      // Wrap in both directions. Shift+Tab off the first element is the half
      // that gets forgotten, and it is the half that strands people.
      if (event.shiftKey && (active === first || !panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => { document.removeEventListener("keydown", onKeyDown); };
  }, []);

  // Follow the stream, but only from the bottom. Yanking someone back down
  // while they are reading an earlier answer is worse than not following.
  useEffect(() => {
    const log = logRef.current;
    if (!log) return;
    const atBottom = log.scrollHeight - log.scrollTop - log.clientHeight < 120;
    if (atBottom) log.scrollTop = log.scrollHeight;
  }, [turns]);

  const ask = useCallback(
    async (question: string) => {
      const text = question.trim();
      if (!text || sending || !session) return;

      setDraft("");
      setSending(true);
      setTurns((current) => [
        ...current,
        { role: "user", text },
        { role: "assistant", text: "" },
      ]);

      /** Replace the trailing assistant turn as tokens arrive. */
      const updateLast = (patch: (turn: Turn) => Turn) => {
        setTurns((current) => {
          const next = [...current];
          const index = next.length - 1;
          next[index] = patch(next[index]);
          return next;
        });
      };

      await streamMessage(session.session_id, text, {
        onToken: (chunk) => { updateLast((turn) => ({ ...turn, text: turn.text + chunk })); },
        onSources: () => { /* carried in the done receipts */ },
        onDone: (receipts) => { updateLast((turn) => ({ ...turn, receipts })); },
        onError: (messageText) => {
          updateLast((turn) => ({ ...turn, text: messageText, error: true }));
        },
      });

      setSending(false);
      inputRef.current?.focus();
    },
    [sending, session],
  );

  const spring = reduced
    ? { duration: 0 }
    : ({ type: "spring", stiffness: 260, damping: 28 } as const);

  // Filtered before slicing, so re-offering them after a turn never hands
  // someone back a question they just asked.
  const asked = new Set(turns.filter((turn) => turn.role === "user").map((turn) => turn.text));
  const suggestions = (session?.suggestions ?? []).filter((s) => !asked.has(s)).slice(0, 3);
  const disabled = !session?.chat_enabled || sending;

  return (
    <motion.div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="assistant-title"
      initial={reduced ? false : { opacity: 0, y: 24, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduced ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.97 }}
      transition={spring}
      className="glass glass-sheen pointer-events-auto relative flex max-h-[min(38rem,calc(100dvh-3rem))] w-[min(26rem,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-2xl border border-accent-dim shadow-[0_40px_100px_-40px_var(--color-accent)]"
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent to-transparent"
      />

      <header className="flex items-center gap-3 border-b border-edge/70 px-4 py-3.5">
        <span aria-hidden="true" className="relative flex size-8 items-center justify-center">
          <motion.span
            className="absolute -inset-1 rounded-xl bg-glow/40 blur-md"
            animate={reduced ? undefined : { opacity: [0.4, 0.85, 0.4], scale: [1, 1.15, 1] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          <span className="absolute inset-0 rounded-xl border border-accent-dim bg-canvas" />
          <svg viewBox="0 0 40 40" className="relative size-full p-1 text-accent">
            <g fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
              <path d="M20 8V5" />
              <circle cx="20" cy="3.4" r="2" fill="currentColor" stroke="none" />
              <rect x="7" y="9" width="26" height="22" rx="8" />
              <motion.g
                fill="currentColor"
                stroke="none"
                animate={reduced ? undefined : { scaleY: [1, 1, 0.1, 1] }}
                transition={{
                  duration: 4.4,
                  repeat: Infinity,
                  times: [0, 0.9, 0.95, 1],
                  ease: "easeInOut",
                }}
                style={{ transformOrigin: "20px 18px" }}
              >
                <circle cx="14.8" cy="18" r="2.7" />
                <circle cx="25.2" cy="18" r="2.7" />
              </motion.g>
              <path d="M15.5 24.5c2.6 2.2 6.4 2.2 9 0" />
            </g>
          </svg>
        </span>
        <p id="assistant-title" className="flex-1 text-xs text-text">
          Ask about Iftikhar&rsquo;s work
        </p>
        <button
          ref={closeRef}
          type="button"
          onClick={closeChat}
          className="-m-1.5 rounded-full p-1.5 text-text-dim transition-colors duration-200 hover:text-text"
        >
          <span className="sr-only">Close the assistant</span>
          <svg
            aria-hidden="true"
            viewBox="0 0 14 14"
            className="size-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          >
            <path d="M2.5 2.5l9 9M11.5 2.5l-9 9" />
          </svg>
        </button>
      </header>

      <div ref={logRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-5">
        <div className="rounded-2xl rounded-bl-sm border border-edge bg-canvas px-4 py-4">
          <p className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="relative inline-flex size-11 shrink-0 items-center justify-center"
            >
              <motion.span
                className="absolute -inset-1 rounded-2xl bg-glow/35 blur-md"
                animate={reduced ? undefined : { opacity: [0.4, 0.85, 0.4], scale: [1, 1.12, 1] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              />
              <span className="absolute inset-0 rounded-2xl border border-accent-dim bg-surface" />
              <svg viewBox="0 0 40 40" className="relative size-full p-1.5 text-accent">
                <g fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                  <path d="M20 8V4.5" />
                  <circle cx="20" cy="3" r="2.1" fill="currentColor" stroke="none" />
                  <rect x="6.5" y="9" width="27" height="22.5" rx="8" />
                  <path d="M6.5 17H4m29.5 0H36" />
                  <motion.g
                    fill="currentColor"
                    stroke="none"
                    animate={reduced ? undefined : { scaleY: [1, 1, 0.1, 1] }}
                    transition={{
                      duration: 4.4,
                      repeat: Infinity,
                      times: [0, 0.9, 0.95, 1],
                      ease: "easeInOut",
                    }}
                    style={{ transformOrigin: "20px 18px" }}
                  >
                    <circle cx="14.5" cy="18" r="2.7" />
                    <circle cx="25.5" cy="18" r="2.7" />
                  </motion.g>
                  <path d="M15.5 24.5c2.6 2.2 6.4 2.2 9 0" />
                </g>
              </svg>
            </span>

            {/* The wave is the one thing here that moves besides the blink. */}
            <motion.span
              aria-hidden="true"
              className="origin-[70%_80%] text-xl"
              animate={reduced ? undefined : { rotate: [0, 18, -8, 18, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, repeatDelay: 2.4, ease: "easeInOut" }}
            >
              👋
            </motion.span>
          </p>

          {/* "I'm Iftikhar's official AI assistant — I know everything on this
              site" claimed authority and offered nothing. It also set the
              wrong expectation: the useful thing here is not that it has read
              the site, it is that it can put his actual work next to whatever
              you are trying to build. Say that instead, and invite the
              question worth asking. */}
          <p className="mt-3 text-2xs leading-relaxed text-text">
            <span className="text-text">Hi — good to see you.</span>
            <br />I answer from Iftikhar&rsquo;s real project records: what he
            built, how it works, and what it cost to get right.
            <br />
            Tell me what you&rsquo;re building and I&rsquo;ll show you the
            closest thing he&rsquo;s shipped.
          </p>
        </div>

        {waking && (
          <p className="text-2xs text-text-dim">Waking the server up…</p>
        )}
        {sessionError && (
          <p role="alert" className="text-2xs text-halt">
            {sessionError}
          </p>
        )}
        {session && !session.chat_enabled && (
          <p role="alert" className="text-2xs text-degraded">
            {session.reason ?? "The assistant is off right now."}
          </p>
        )}

        {/* Polite, not assertive: assertive would interrupt a screen reader on
            every token, which is dozens of interruptions per answer. */}
        <div aria-live="polite" className="space-y-4">
          {turns.map((turn, index) =>
            turn.role === "user" ? (
              <p
                key={index}
                className="ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-accent/10 px-3.5 py-2.5 text-2xs leading-relaxed text-text"
              >
                {turn.text}
              </p>
            ) : (
              <div key={index} className="max-w-[92%]">
                <AnswerBubble turn={turn} linkedinUrl={linkedinUrl} reduced={reduced} />
              </div>
            ),
          )}
        </div>

        {/* Kept up while the conversation is still finding its feet, not just
            on an empty panel. Someone who opened with "hello" used to watch
            the only three concrete things to ask about disappear the moment
            they said it — leaving a blank box and the burden of thinking of a
            question. They go away once the conversation is genuinely running. */}
        {turns.length < 4 && !sending && suggestions.length > 0 && (
          <>
            <p className="mt-6 mb-2.5 font-mono text-[0.6875rem] tracking-[0.22em] text-text-dim uppercase">
              {turns.length === 0 ? "Try" : "Or ask"}
            </p>
            <ul className="space-y-2">
              {suggestions.map((opener, index) => (
                <motion.li
                  key={opener}
                  initial={reduced ? false : { opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={
                    reduced ? { duration: 0 } : { delay: 0.18 + index * 0.07, duration: 0.4 }
                  }
                >
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => void ask(opener)}
                    className="w-full rounded-xl border border-edge px-3.5 py-2.5 text-left text-2xs text-text-dim transition-colors duration-200 hover:border-accent-dim hover:text-text disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {opener}
                  </button>
                </motion.li>
              ))}
            </ul>
          </>
        )}
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void ask(draft);
        }}
        className="border-t border-edge/70 p-3"
      >
        <div className="flex items-end gap-2">
          <label htmlFor="assistant-input" className="sr-only">
            Ask a question about Iftikhar&rsquo;s work
          </label>
          <textarea
            id="assistant-input"
            ref={inputRef}
            rows={1}
            value={draft}
            disabled={disabled}
            onChange={(event) => { setDraft(event.target.value); }}
            onKeyDown={(event) => {
              // Enter sends, Shift+Enter breaks the line. A textarea is used
              // rather than an input so the second half of that is possible.
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                void ask(draft);
              }
            }}
            placeholder={session?.chat_enabled ? "Ask anything…" : "Unavailable"}
            className="max-h-28 min-h-[2.5rem] flex-1 resize-none rounded-xl border border-line bg-canvas px-3 py-2 text-2xs text-text transition-colors duration-200 focus:border-accent disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={disabled || draft.trim().length === 0}
            className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-accent-dim text-accent transition-colors duration-200 hover:bg-accent/10 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <span className="sr-only">Send</span>
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="size-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14m-6-6 6 6-6 6" />
            </svg>
          </button>
        </div>
      </form>
    </motion.div>
  );
}

export function ChatPanel() {
  const open = useChatOpen();

  // Nothing behind the panel scrolls while it is open on a phone, where it
  // covers most of the screen. On desktop it is a corner panel, so the page
  // stays scrollable — locking there would be a bug, not a courtesy.
  useEffect(() => {
    if (!open) return;
    const isSmall = window.matchMedia("(max-width: 639px)").matches;
    if (!isSmall) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  return (
    <div className="pointer-events-none fixed right-5 bottom-5 z-50 sm:right-8 sm:bottom-8">
      <AnimatePresence>{open && <Panel />}</AnimatePresence>
    </div>
  );
}
